import { useRequisitions } from '@hook'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@modal'
import { RequisitionData, RequisitionIncluded } from '@type/requisitions.type'
import { Badge, Button, SideDrawer } from '@ui'
import InformationList from '@ui/InformationList'
import { showToast } from '@util'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { useAppSelector } from '../../../../../../../store/hooks'
import { getRequisitionItems, statusLabel } from '../../../requisitions.helpers'

type Props = {
  onClose: () => void
  requisitionId?: string
  defaultData?: RequisitionData
  included?: RequisitionIncluded[]
}

const itemLabel = (item: any) => {
  const brand = item.brand_name?.trim()
  const generic = item.generic_name?.trim()
  const qty = item.approved_quantity === '0.0' ? item.requested_quantity : item.approved_quantity

  const parts = [
    brand ? `BN: ${brand}` : '',
    generic ? `GN: ${generic}` : '',
    qty !== undefined && qty !== null && String(qty) !== '' ? `QTY: ${qty}` : ''
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : 'Unnamed drug'
}

const RequisitionDetails = ({ onClose, requisitionId = '', defaultData, included = [] }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { openModal } = useModalStore()
  const { fulfillRequisition } = useRequisitions()
  const currentStaffId = useAppSelector(state => state.user.user?.authable_id)

  const requisition = defaultData
  const requisitionIncluded = included

  const selectedRequestedBy = useMemo(
    () => requisition?.relationships?.requested_by?.data?.id,
    [requisition?.relationships?.requested_by?.data?.id]
  )
  const selectedReviewedBy = useMemo(
    () => requisition?.relationships?.reviewed_by?.data?.id,
    [requisition?.relationships?.reviewed_by?.data?.id]
  )
  const selectedUnit = useMemo(
    () => requisition?.relationships?.unit?.data?.id,
    [requisition?.relationships?.unit?.data?.id]
  )

  const selectedRequestedByName = useMemo(
    () => requisitionIncluded.find(item => item.type === 'staff' && item.id === selectedRequestedBy)?.attributes,
    [requisitionIncluded, selectedRequestedBy]
  )
  const selectedReviewedByName = useMemo(
    () => requisitionIncluded.find(item => item.type === 'staff' && item.id === selectedReviewedBy)?.attributes,
    [requisitionIncluded, selectedReviewedBy]
  )
  const selectedUnitName = useMemo(
    () => requisitionIncluded.find(item => item.type === 'unit' && item.id === selectedUnit)?.attributes,
    [requisitionIncluded, selectedUnit]
  )

  const status = requisition?.attributes.status
  // Outbound only for the requester; everyone else (same unit or not) gets inbound actions.
  const isOutbound = selectedRequestedBy === `${currentStaffId}`
  const showAssessButton = !isOutbound && (status === 'pending_review' || status === 'partially_approved')
  const showFulfillButton = !isOutbound && (status === 'approved' || status === 'partially_approved')

  const requisitionItems = getRequisitionItems(requisition, requisitionIncluded)

  const drugDetails = requisitionItems.length
    ? requisitionItems.reduce<Record<string, string>>((acc, item, index) => {
        const drugName = `Drug Item ${index + 1}`
        acc[drugName] = itemLabel(item)
        return acc
      }, {})
    : {}

  const otherDetails = {
    'Order ID': requisition?.id || '-',
    'Ordered by': selectedRequestedByName
      ? `${selectedRequestedByName.first_name || ''} ${selectedRequestedByName.last_name || ''}`
      : '-',
    Unit: selectedUnitName?.name || '-',
    'Start Date': requisition?.attributes.created_at
      ? moment(requisition.attributes.created_at).format('DD/MM/YYYY')
      : '-',
    'Reviewed by': selectedReviewedByName
      ? `${selectedReviewedByName.first_name || ''} ${selectedReviewedByName.last_name || ''}`
      : '-',
    Status: <Badge label={statusLabel(status)} size="small" status={status} />
  }

  const handleButtonClick = async () => {
    if (status === 'pending_review') {
      onClose()
      openModal(PHARMACY_MODAL_TYPE.REQUISITION_ASSESS, {
        requisitionId,
        defaultData: requisition,
        included: requisitionIncluded
      })
      return
    }

    if (!requisitionId) return

    setIsSubmitting(true)
    try {
      await fulfillRequisition(requisitionId)
      showToast('Requisition fulfilled successfully')
      onClose()
    } catch (error) {
      console.error(error)
      showToast('Unable to fulfill requisition')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SideDrawer onClose={onClose} title="Requisition Details">
      <div className="p-6 flex flex-col gap-6">
        {requisitionItems.length > 0 && (
          <div>
            <InformationList data={drugDetails} title="Drug Details" />
          </div>
        )}

        <div>
          <InformationList data={otherDetails} title="Other Details" />
        </div>

        {showAssessButton || showFulfillButton ? (
          <div className="pt-4 border-t border-[#E5E5E5] flex flex-wrap gap-3">
            {showAssessButton && (
              <Button
                label="Assess request"
                onClick={() => {
                  onClose()
                  openModal(PHARMACY_MODAL_TYPE.REQUISITION_ASSESS, {
                    requisitionId,
                    defaultData: requisition,
                    included: requisitionIncluded
                  })
                }}
                size="wide"
              />
            )}
            {showFulfillButton && (
              <Button isLoading={isSubmitting} label="Fulfill request" onClick={handleButtonClick} size="wide" />
            )}
          </div>
        ) : null}
      </div>
    </SideDrawer>
  )
}

export default RequisitionDetails
